'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Button from './Button';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

/* Standalone (not the shared <Button>) so the green active state doesn't
   fight Button's navy HOVER_NAVY. Same border + icon shape as other icon
   buttons, but the color theme is favorites-specific. */
const BTN_BASE =
  'inline-flex items-center justify-center rounded-lg border transition-colors duration-200 ease-out select-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-60';
const BTN_ACTIVE =
  'bg-green-5 text-green-50 border-green-15 ' +
  'hover:bg-green-50 hover:text-constant-white hover:border-green-50 ' +
  'focus-visible:bg-green-50 focus-visible:text-constant-white focus-visible:border-green-50 ' +
  'active:bg-green-60 active:text-constant-white active:border-green-60';
const BTN_INACTIVE =
  'bg-constant-white text-blue-100 border-neutral-10 ' +
  'hover:bg-green-50 hover:text-constant-white hover:border-green-50 ' +
  'focus-visible:bg-green-50 focus-visible:text-constant-white focus-visible:border-green-50 ' +
  'active:bg-green-60 active:text-constant-white active:border-green-60';

type Props = { productId: string; className?: string; size?: number };

export default function FavoriteButton({ productId, className = '', size = 20 }: Props) {
  const { isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  const authHeaders = useCallback((): Record<string, string> => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !productId) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/auth/account/favorites/${productId}/status`, {
      headers: authHeaders(),
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setIsFavorite(!!d.isFavorite); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated, productId, authHeaders]);

  if (!isAuthenticated) return null;

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const next = !isFavorite;
    setIsFavorite(next);
    try {
      const res = await fetch(`${API_BASE}/api/auth/account/favorites/${productId}`, {
        method: next ? 'POST' : 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) setIsFavorite(!next);
    } catch {
      setIsFavorite(!next);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      raw
      type="button"
      onClick={toggle}
      disabled={loading}
      data-no-lift
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
      className={[
        BTN_BASE,
        isFavorite ? BTN_ACTIVE : BTN_INACTIVE,
        className,
      ].filter(Boolean).join(' ')}
      style={{ width: size + 16, height: size + 16 }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill={isFavorite ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </Button>
  );
}
